import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { bestHand, BLINDS, catOf, equity, EXTRA_EMOTES, handName, HERO, HEROES, RANK_SYM, TITLES } from '@aleaces/engine';
import type { Card, ClientEvent, ReportReason, TableSnapshot } from '@aleaces/protocol';
import { CardView } from '../components/CardView';
import { DeckDeal } from '../components/DeckDeal';
import { Portrait } from '../components/Portrait';
import { useMuted, useProfile } from '../hooks';
import { net } from '../net';
import { owns } from '../profile';
import { toggleMuted } from '../settings';
import { SFX } from '../sfx';
import { toast } from '../toast';

const SEAT_POS: [number, number][] = [[50, 93], [10, 66], [14, 22], [50, 4], [86, 22], [90, 66]];
/* flat landscape phones: seats hug the rail so nothing collides with board or hud */
const SEAT_POS_FLAT: [number, number][] = [[50, 97], [9, 74], [12, 12], [50, -4], [88, 12], [91, 74]];
const cardId = (c: Card) => c.r * 4 + c.s;

interface Bubble { text: string; cls?: string; key: number }

const FLAT_QUERY = '(max-height: 480px)';
/** true on flat landscape-phone screens; re-renders when it changes */
function useFlatScreen(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(FLAT_QUERY);
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    },
    () => window.matchMedia(FLAT_QUERY).matches,
  );
}

export function MatchScreen({ snapshot, onLeave }: { snapshot: TableSnapshot; onLeave: () => void }) {
  const p = useProfile();
  const muted = useMuted();
  const [actionBubbles, setActionBubbles] = useState<Record<number, Bubble>>({});
  const [emoteBubbles, setEmoteBubbles] = useState<Record<number, Bubble>>({});
  const [banner, setBanner] = useState<Bubble | null>(null);
  const [winners, setWinners] = useState<number[]>([]);
  const [dealerDealing, setDealerDealing] = useState(false);
  const [hlCards, setHlCards] = useState<Set<number>>(new Set());
  const [peeks, setPeeks] = useState<Record<number, { index: number; card: Card }>>({});
  const [emoteOpen, setEmoteOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const bubbleKey = useRef(1);

  const isFlat = useFlatScreen();
  const seatPos = isFlat ? SEAT_POS_FLAT : SEAT_POS;
  const mySeat = snapshot.you?.seat ?? -1;
  const me = snapshot.players.find((q) => q.seat === mySeat);
  const myHero = me ? HERO[me.heroId] : undefined;
  const myTurn = snapshot.toAct !== null && snapshot.toAct === mySeat && !!snapshot.you?.legal;

  /** stereo position of a seat, relative to where it is rendered */
  function panForSeat(seat: number): number {
    const my = snapshotRef.current.you?.seat ?? 0;
    const rel = ((seat - my) % 6 + 6) % 6;
    const x = SEAT_POS[rel]?.[0] ?? 50;
    return Math.max(-0.8, Math.min(0.8, (x - 50) / 65));
  }

  // ---------- event handling (sfx, bubbles, banner, highlights) ----------
  useEffect(() => {
    return net.onMessage((msg) => {
      if (msg.type !== 'events') return;
      for (const e of msg.events) handleEvent(e);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mySeat]);

  function pushAction(seat: number, text: string, cls?: string) {
    setActionBubbles((b) => ({ ...b, [seat]: { text, cls, key: bubbleKey.current++ } }));
  }

  function handleEvent(e: ClientEvent) {
    switch (e.type) {
      case 'handStart':
        setWinners([]);
        setHlCards(new Set());
        setPeeks({});
        setActionBubbles({});
        setDealerDealing(true);
        setTimeout(() => setDealerDealing(false), 1500);
        SFX.shuffle();
        break;
      case 'deal': {
        // one deal tick per card, timed like the staggered deal-in animation
        const inHand = snapshotRef.current.players.filter((q) => q.inHand && !q.out);
        if (inHand.length === 0) {
          SFX.deal();
        } else {
          for (const q of inHand) {
            const rel = ((q.seat - (mySeat >= 0 ? mySeat : 0)) % 6 + 6) % 6;
            for (let i = 0; i < 2; i++) SFX.deal((i * 130 + rel * 50) / 1000, panForSeat(q.seat));
          }
        }
        setTimeout(() => SFX.flip(), 550);
        break;
      }
      case 'action': {
        const label =
          e.kind === 'fold' ? 'Fold' :
          e.kind === 'check' ? 'Check' :
          e.kind === 'call' ? 'Call ' + e.amount :
          e.kind === 'bet' ? 'Bet ' + e.to :
          e.kind === 'raise' ? 'Raise ' + e.to :
          'All-in ' + e.to;
        const cls = e.kind === 'fold' ? 'fold' : e.kind === 'bet' || e.kind === 'raise' || e.kind === 'allin' ? 'raise' : undefined;
        pushAction(e.seat, label, cls);
        const pan = panForSeat(e.seat);
        if (e.kind === 'fold') SFX.fold(pan);
        else if (e.kind === 'check') SFX.check(pan);
        else if (e.kind === 'allin') SFX.allin(pan);
        else SFX.chip(e.amount, pan);
        break;
      }
      case 'street':
        SFX.flip();
        break;
      case 'roar': {
        const seatP = snapshotRef.current.players.find((q) => q.seat === e.seat);
        const nm = seatP ? (HERO[seatP.heroId]?.name.split(' ')[0] ?? seatP.name) : '';
        setBanner({ text: nm + ': KRIEGSSCHREI!', key: bubbleKey.current++ });
        SFX.allin();
        break;
      }
      case 'ability': {
        const ab = Object.values(HERO).find((h) => h.ability.id === e.abilityId)?.ability;
        if (ab) pushAction(e.seat, ab.icon + ' ' + ab.name, 'raise');
        SFX.emote();
        break;
      }
      case 'peek': {
        setPeeks((prev) => ({ ...prev, [e.targetSeat]: { index: e.cardIndex, card: e.card } }));
        const target = snapshotRef.current.players.find((q) => q.seat === e.targetSeat);
        toast('Flinke Finger', 'Du hast eine Karte von ' + (target?.name.split(' ')[0] ?? 'deinem Gegner') + ' gesehen.');
        break;
      }
      case 'abilityPayout': {
        const label = e.abilityId === 'magnet' ? 'Chipmagnet +' + e.amount : 'Rabatt +' + e.amount;
        pushAction(e.seat, label, 'raise');
        if (e.seat === mySeat) toast(e.abilityId === 'magnet' ? 'Chipmagnet' : 'Bierdeckel-Rabatt', '+' + e.amount + ' Chips');
        break;
      }
      case 'shield':
        pushAction(e.seat, 'Schild der Ehre: ' + e.chips, 'raise');
        if (e.seat === mySeat) toast('Schild der Ehre', 'Du bleibst mit ' + e.chips + ' Chips am Tisch.');
        break;
      case 'showdown': {
        setWinners(e.winners.map((w) => w.seat));
        const ids = new Set<number>();
        for (const w of e.winners) for (const c of w.cards) ids.add(cardId(c));
        setHlCards(ids);
        const top = e.winners[0];
        if (top) {
          const wp = snapshotRef.current.players.find((q) => q.seat === top.seat);
          const nm = top.seat === mySeat ? 'Du gewinnst' : (wp?.name.split(' ')[0] ?? '') + ' gewinnt';
          setBanner({ text: (e.split ? 'Geteilter Pot' : nm) + ' · ' + top.handName, key: bubbleKey.current++ });
        }
        const mine = e.winners.find((w) => w.seat === mySeat);
        if (mine) SFX.win(mine.amount >= 600 || e.winners.some((w) => w.seat === mySeat && catOfName(w.handName) >= 5));
        else if (snapshotRef.current.players.find((q) => q.seat === mySeat)?.inHand) SFX.lose();
        else SFX.chip(e.potTotal);
        break;
      }
      case 'winWithoutShowdown': {
        setWinners([e.seat]);
        const wp = snapshotRef.current.players.find((q) => q.seat === e.seat);
        setBanner({
          text: (e.seat === mySeat ? 'Du gewinnst' : (wp?.name.split(' ')[0] ?? '') + ' gewinnt') + ' ' + e.amount,
          key: bubbleKey.current++,
        });
        if (e.seat === mySeat) SFX.win(e.amount >= 600);
        else SFX.chip(e.amount);
        break;
      }
      case 'eliminated':
        if (e.seat === mySeat) setBanner({ text: 'Ausgeschieden – Platz ' + e.placement, key: bubbleKey.current++ });
        break;
      case 'emote':
        setEmoteBubbles((b) => ({ ...b, [e.seat]: { text: e.text, key: bubbleKey.current++ } }));
        SFX.emote(panForSeat(e.seat));
        break;
      default:
        break;
    }
  }

  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const tableRef = useRef<HTMLDivElement>(null);

  // turn timer tick
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);

  // keyboard shortcuts F / C, Escape closes dialogs
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setConfirmLeave(false);
        setEmoteOpen(false);
        setReportOpen(false);
        return;
      }
      if (!snapshotRef.current.you?.legal || snapshotRef.current.toAct !== snapshotRef.current.you.seat) return;
      const k = e.key.toLowerCase();
      const legal = snapshotRef.current.you.legal;
      if (k === 'f') act({ type: 'fold' });
      if (k === 'c') act(legal.canCheck ? { type: 'check' } : { type: 'call' });
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, []);

  function act(action: { type: 'fold' } | { type: 'check' } | { type: 'call' } | { type: 'raise'; to: number }) {
    net.send({ type: 'act', action });
  }

  // ---------- derived ----------
  const alive = snapshot.players.filter((q) => !q.out).length;
  const showOdds = !!snapshot.you?.abilityActive && myHero?.ability.id === 'odds';

  const myHandText = useMemo(() => {
    const you = snapshot.you;
    if (!you || !me?.inHand || you.hole.length < 2) return '';
    const all = you.hole.concat(snapshot.board);
    let text: string;
    if (all.length < 5) {
      const [a, b] = [you.hole[0]!, you.hole[1]!];
      text = a.r === b.r ? 'Paar ' + RANK_SYM[a.r] : RANK_SYM[a.r]! + RANK_SYM[b.r] + (a.s === b.s ? ' suited' : ' offsuit');
    } else {
      text = handName(bestHand(all).score);
    }
    if (showOdds) {
      const n = snapshot.players.filter((q) => q.inHand).length - 1;
      if (n >= 1) {
        const eq = equity(you.hole, snapshot.board, n, 400);
        text += ' · ' + Math.round(eq * 100) + ' % Gewinnchance';
      }
    }
    return text;
  }, [snapshot, me, showOdds]);

  const secondsLeft = snapshot.actDeadline ? Math.max(0, Math.ceil((snapshot.actDeadline - now) / 1000)) : null;

  const emoteList = useMemo(() => {
    const heroEmotes = me ? HERO[me.heroId]?.emotes ?? [] : [];
    return [...heroEmotes, ...EXTRA_EMOTES.filter((e) => owns('emote', e.id, p)).map((e) => e.text)];
  }, [me, p]);

  const blinds = BLINDS[snapshot.level] ?? [10, 20];

  if (!p) return null;
  const cardback = p.cardback;
  const felt = feltColors(p.felt);

  return (
    <div className="match-screen" onClick={() => setEmoteOpen(false)}>
      <div className="match-top">
        <span className="mlogo">Ale &amp; Aces</span>
        <span className="muted">·</span>
        <span>{snapshot.mode === 'ranked' ? 'Ranglistenturnier' : 'Freundschaftsrunde'}</span>
        <span className="muted">·</span>
        <span>Hand <b>{snapshot.handNo}</b></span>
        <span className="muted">·</span>
        <span>Blinds <b>{blinds[0]}/{blinds[1]}</b></span>
        <span className="muted">·</span>
        <span>Spieler <b>{alive}</b>/{snapshot.players.length}</span>
        <span className="spacer" />
        <button className="btn ghost sm" onClick={() => toggleMuted()}>
          {muted ? '🔇 Stumm' : '🔊 Ton'}
        </button>
        <button className="btn ghost sm" onClick={() => setReportOpen(true)}>Melden</button>
        <button className="btn sm danger" onClick={() => setConfirmLeave(true)}>Tisch verlassen</button>
      </div>
      <div className="table-wrap">
        <div
          className="table"
          ref={tableRef}
          style={{
            ['--felt' as string]: felt.base,
            ['--felt-hi' as string]: felt.hi,
            ['--felt-edge' as string]: felt.edge,
          }}
        >
          <div className="rail" />
          <div className="felt" />
          <DeckDeal dealing={dealerDealing} />
          <div className={'potline' + (snapshot.potTotal === 0 ? ' empty' : '')}>
            <span className="chip green" /><span className="chip blue" /> {snapshot.potTotal}
          </div>
          <div className="center">
            <div className="board">
              {snapshot.board.map((c, i) => (
                <CardView key={snapshot.handNo + '-' + cardId(c)} card={c} up lg dealing cardback={cardback} hl={hlCards.has(cardId(c))} />
              ))}
            </div>
          </div>
          {banner && <div className="banner show" key={banner.key}>{banner.text}</div>}
          {snapshot.players.map((pl) => {
            const rel = ((pl.seat - (mySeat >= 0 ? mySeat : 0)) % 6 + 6) % 6;
            const [x, y] = seatPos[rel] ?? [50, 50];
            const isMe = pl.seat === mySeat;
            const folded = !pl.inHand && !pl.out && snapshot.handNo > 0 && pl.cardCount > 0;
            const acting = snapshot.toAct === pl.seat;
            const aBubble = actionBubbles[pl.seat];
            const eBubble = emoteBubbles[pl.seat];
            const peek = peeks[pl.seat];
            const title = isMe
              ? (TITLES.find((t) => t.id === p.title) ?? TITLES[0]!).name
              : pl.title;
            const cards: { card: Card | null; up: boolean; hl: boolean }[] = [];
            if (isMe && snapshot.you) {
              for (const c of snapshot.you.hole) cards.push({ card: c, up: true, hl: hlCards.has(cardId(c)) });
            } else if (pl.revealed) {
              for (const c of pl.revealed) cards.push({ card: c, up: true, hl: hlCards.has(cardId(c)) });
            } else {
              for (let i = 0; i < pl.cardCount; i++) {
                if (peek && peek.index === i) cards.push({ card: peek.card, up: true, hl: true });
                else cards.push({ card: null, up: false, hl: false });
              }
            }
            return (
              <div
                key={pl.seat}
                className={[
                  'seat',
                  isMe ? 'human' : '',
                  pl.out ? 'out' : '',
                  folded ? 'folded' : '',
                  acting ? 'acting' : '',
                  winners.includes(pl.seat) ? 'winner' : '',
                  snapshot.buttonSeat === pl.seat ? 'dealer' : '',
                  !pl.connected && !pl.isBot ? 'disconnected' : '',
                ].filter(Boolean).join(' ')}
                style={{ left: x + '%', top: y + '%' }}
              >
                {eBubble && <div className="bubble emote show" key={'e' + eBubble.key}>{eBubble.text}</div>}
                <div className="seat-cards">
                  {cards.map((c, i) => (
                    <CardView
                      key={snapshot.handNo + '-' + i + '-' + (c.card ? cardId(c.card) : 'b')}
                      card={c.card}
                      up={c.up}
                      lg={isMe}
                      dim={folded}
                      hl={c.hl}
                      cardback={cardback}
                      dealing={snapshot.board.length === 0}
                      delayMs={i * 130 + rel * 50}
                    />
                  ))}
                </div>
                {isMe && myHandText && <div className="handlabel">{myHandText}</div>}
                <div className="avatar">
                  <Portrait heroId={pl.heroId} skinId={pl.skinId} frame={isMe ? p.frame : pl.frame} />
                  <div className="dbtn">D</div>
                </div>
                <div className="plate">
                  <div className="pname">{isMe ? pl.name + ' · ' + title : pl.name}{!pl.connected && !pl.isBot ? ' (getrennt)' : ''}</div>
                  <div className={'pchips' + (pl.allIn && pl.inHand ? ' allin' : '')}>{pl.chips}</div>
                </div>
                {pl.roundBet > 0 && (
                  <div className="bet show" key={'bet' + pl.roundBet} style={betPos(x, y)}>
                    <span className={'chip ' + (pl.roundBet >= 500 ? 'black' : pl.roundBet >= 200 ? 'green' : pl.roundBet >= 50 ? 'blue' : '')} />
                    <span>{pl.roundBet}</span>
                  </div>
                )}
                {aBubble && <div className={'bubble action show ' + (aBubble.cls ?? '')} key={'a' + aBubble.key}>{aBubble.text}</div>}
              </div>
            );
          })}
          {winners.map((seat) => {
            const el = tableRef.current;
            if (!el) return null;
            const rel = ((seat - (mySeat >= 0 ? mySeat : 0)) % 6 + 6) % 6;
            const [x, y] = seatPos[rel] ?? [50, 50];
            const dx = ((x - 50) / 100) * el.clientWidth;
            const dy = ((y - 44) / 100) * el.clientHeight;
            return (
              <div key={'fly' + seat} className="win-fly" style={{ ['--dx' as string]: dx + 'px', ['--dy' as string]: dy + 'px' }}>
                {['', 'blue', 'green', 'black'].map((c, i) => (
                  <span key={i} className={('chip ' + c).trim()} style={{ animationDelay: i * 90 + 'ms' }} />
                ))}
              </div>
            );
          })}
        </div>
      </div>
      <div className="hud" onClick={(e) => e.stopPropagation()}>
        <div className="hint">
          {me?.out
            ? 'Du bist ausgeschieden. Du kannst zuschauen oder den Tisch verlassen.'
            : myTurn && snapshot.you?.legal
              ? snapshot.you.legal.toCall > 0
                ? <><b>{snapshot.currentBet}</b> zu callen ({snapshot.you.legal.toCall} mehr). Pot: <b>{snapshot.potTotal}</b></>
                : <>Du bist dran. Pot: <b>{snapshot.potTotal}</b></>
              : 'Warte auf die anderen Spieler …'}
        </div>
        <div className="handname">{myHandText}</div>
        <div className="hud-side">
          {me && !me.out && myHero && snapshot.you && (
            <button
              className={'btn sm' + (!snapshot.you.abilityUsed && me.inHand ? ' gold' : '')}
              disabled={snapshot.you.abilityUsed || !me.inHand}
              title={myHero.ability.desc}
              onClick={() => net.send({ type: 'useAbility' })}
            >
              {myHero.ability.icon} {myHero.ability.name}
              {snapshot.you.abilityUsed ? (snapshot.you.abilityActive ? ' (aktiv)' : ' (verbraucht)') : ''}
            </button>
          )}
          <div className="emote-btn">
            <button className="btn ghost sm" aria-expanded={emoteOpen} aria-haspopup="menu" onClick={(e) => { e.stopPropagation(); setEmoteOpen((o) => !o); }}>Emote</button>
            <div className={'emote-menu' + (emoteOpen ? ' open' : '')}>
              {emoteList.map((t, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setEmoteOpen(false);
                    net.send({ type: 'emote', text: t });
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="hud-main">
          {myTurn && secondsLeft !== null && (
            <div className="turn-timer">⏳ <b>{secondsLeft}s</b></div>
          )}
          {myTurn && snapshot.you?.legal && <ActionBar legal={snapshot.you.legal} currentBet={snapshot.currentBet} pot={snapshot.potTotal} bb={blinds[1]!} onAct={act} />}
        </div>
      </div>
      {reportOpen && (
        <ReportModal
          players={snapshot.players.filter((q) => q.seat !== mySeat && !q.isBot).map((q) => ({ seat: q.seat, name: q.name }))}
          onClose={() => setReportOpen(false)}
        />
      )}
      {confirmLeave && (
        <div className="modal" role="dialog" aria-modal="true" aria-labelledby="leave-title" onClick={() => setConfirmLeave(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h2 id="leave-title">Tisch verlassen?</h2>
            <p className="muted" style={{ margin: '8px 0 18px' }}>
              Das zählt als Ausscheiden auf deinem aktuellen Platz. Erfahrung und Taler für die bisher gespielten Hände behältst du.
              Ein Wirtshaus-Held übernimmt deinen Platz.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn danger" onClick={onLeave}>Tisch verlassen</button>
              <button className="btn ghost" autoFocus onClick={() => setConfirmLeave(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const REPORT_REASONS: { id: ReportReason; label: string }[] = [
  { id: 'abuse', label: 'Beleidigung / Verhalten' },
  { id: 'collusion', label: 'Absprache' },
  { id: 'bot', label: 'Bot-Verdacht' },
  { id: 'other', label: 'Sonstiges' },
];

function ReportModal({ players, onClose }: { players: { seat: number; name: string }[]; onClose: () => void }) {
  const [seat, setSeat] = useState<number | null>(players[0]?.seat ?? null);
  const [reason, setReason] = useState<ReportReason>('abuse');
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="report-title" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <h2 id="report-title">Spieler melden</h2>
        {players.length === 0 ? (
          <>
            <p className="muted" style={{ margin: '8px 0 18px', fontFamily: 'var(--font-ui)', fontSize: 14 }}>
              An diesem Tisch sitzen nur Wirtshaus-Helden – hier gibt es niemanden zu melden.
            </p>
            <button className="btn ghost" autoFocus onClick={onClose}>Schließen</button>
          </>
        ) : (
          <>
            <div style={{ display: 'grid', gap: 10, margin: '10px 0 18px', fontFamily: 'var(--font-ui)', fontSize: 14, textAlign: 'left' }}>
              <label style={{ display: 'grid', gap: 4 }}>
                Wen möchtest du melden?
                <select
                  autoFocus
                  value={seat ?? undefined}
                  onChange={(e) => setSeat(+e.target.value)}
                  style={{ background: 'rgba(0,0,0,.35)', border: '1px solid var(--panel-line)', color: 'var(--parch)', borderRadius: 6, padding: '8px 10px' }}
                >
                  {players.map((p) => <option key={p.seat} value={p.seat}>{p.name}</option>)}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                Grund
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value as ReportReason)}
                  style={{ background: 'rgba(0,0,0,.35)', border: '1px solid var(--panel-line)', color: 'var(--parch)', borderRadius: 6, padding: '8px 10px' }}
                >
                  {REPORT_REASONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                className="btn danger"
                onClick={() => {
                  if (seat !== null) net.send({ type: 'report', targetSeat: seat, reason });
                  onClose();
                }}
              >
                Meldung abschicken
              </button>
              <button className="btn ghost" onClick={onClose}>Abbrechen</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function catOfName(name: string): number {
  const names = ['Hohe Karte', 'Ein Paar', 'Zwei Paare', 'Drilling', 'Straße', 'Flush', 'Full House', 'Vierling', 'Straight Flush', 'Royal Flush'];
  return Math.max(0, names.indexOf(name));
}

function betPos(x: number, y: number): React.CSSProperties {
  // --tossX/--tossY make the chip fly in from the player's side (betToss)
  if (y > 60) return { top: -34, ['--tossY' as string]: '30px' };
  if (y < 20) return { bottom: -30, ['--tossY' as string]: '-30px' };
  const side: React.CSSProperties = { top: '40%' };
  if (x < 50) {
    side.right = -12;
    (side as Record<string, unknown>)['--tossX'] = '-34px';
  } else {
    side.left = -12;
    (side as Record<string, unknown>)['--tossX'] = '34px';
  }
  return side;
}

function feltColors(feltId: string): { hi: string; base: string; edge: string } {
  const FELT_MAP: Record<string, { hi: string; base: string; edge: string }> = {
    green: { hi: '#276046', base: '#1c4634', edge: '#0f2a1f' },
    wine: { hi: '#6a2a3a', base: '#4a1a28', edge: '#2a0c14' },
    night: { hi: '#2a3a6a', base: '#1a2648', edge: '#0c1226' },
    moss: { hi: '#4a6a2a', base: '#334a1c', edge: '#1a2a0c' },
    royal: { hi: '#5a2a6a', base: '#3e1a4a', edge: '#220c2a' },
  };
  return FELT_MAP[feltId] ?? FELT_MAP['green']!;
}

function ActionBar({ legal, currentBet, pot, bb, onAct }: {
  legal: { toCall: number; minTo: number; maxTo: number; canRaise: boolean; canCheck: boolean };
  currentBet: number;
  pot: number;
  bb: number;
  onAct: (a: { type: 'fold' } | { type: 'check' } | { type: 'call' } | { type: 'raise'; to: number }) => void;
}) {
  const [raiseTo, setRaiseTo] = useState(legal.minTo);
  useEffect(() => setRaiseTo(legal.minTo), [legal.minTo, legal.maxTo]);
  const clamp = (v: number) => Math.max(legal.minTo, Math.min(legal.maxTo, Math.round(v)));
  const isAllInCall = legal.toCall >= legal.maxTo - currentBet && legal.toCall > 0 && legal.maxTo <= currentBet;
  return (
    <div className="actions">
      {legal.canRaise && (
        <div className="raise-rail">
          <button className="q" onClick={() => setRaiseTo(legal.maxTo)}>All-in</button>
          <button className="q" onClick={() => setRaiseTo(clamp(currentBet + pot + legal.toCall))}>Pot</button>
          <button className="q" onClick={() => setRaiseTo(clamp(currentBet + (pot + legal.toCall) / 2))}>½ Pot</button>
          <input
            type="range"
            aria-label="Erhöhungsbetrag"
            min={legal.minTo}
            max={legal.maxTo}
            step={Math.max(1, Math.floor(bb / 2))}
            value={raiseTo}
            onChange={(e) => setRaiseTo(+e.target.value)}
          />
        </div>
      )}
      <button className="abtn fold" onClick={() => onAct({ type: 'fold' })}>Aussteigen</button>
      {legal.canCheck ? (
        <button className="abtn check" onClick={() => onAct({ type: 'check' })}>Schieben</button>
      ) : (
        <button className="abtn call" onClick={() => onAct({ type: 'call' })}>
          <span className="amt-tag">{legal.toCall}</span>
          {isAllInCall ? 'All-in' : 'Call'}
        </button>
      )}
      {legal.canRaise && (
        <button className="abtn confirm" onClick={() => onAct({ type: 'raise', to: raiseTo })}>
          <span className="amt-tag">{raiseTo}</span>
          Bestätigen
        </button>
      )}
    </div>
  );
}
